/**
 * Does a file match an <input accept="..."> string? The browser's file picker applies `accept`,
 * but a file DROPPED on the page bypasses it, so every drop zone checks again here.
 *
 * Supported tokens: ".pdf" (extension), "application/pdf" (exact type), "image/*" (type family).
 * "audio/*" and "video/*" are not enforced: phones and Windows often give those files no type at all.
 */

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "jfif", "png", "gif", "webp", "bmp", "svg", "ico", "avif", "heic", "heif", "tif", "tiff"];

const extensionOf = (name: string) => (name.toLowerCase().match(/\.([^./\\]+)$/) ?? [])[1] ?? "";

export function matchesAccept(file: { name: string; type: string }, accept: string | undefined): boolean {
  if (!accept) return true;
  const tokens = accept.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean);
  if (tokens.length === 0 || tokens.includes("*") || tokens.includes("*/*")) return true;
  // Wildcards for audio/video are not enforced, see above.
  if (tokens.some((t) => t === "audio/*" || t === "video/*")) return true;

  const ext = extensionOf(file.name);
  const type = (file.type || "").toLowerCase();
  return tokens.some((t) => {
    if (t.startsWith(".")) return ext === t.slice(1) || (t === ".jpg" && ext === "jpeg") || (t === ".jpeg" && ext === "jpg");
    if (t === "image/*") return type.startsWith("image/") || ((!type || type === "application/octet-stream") && IMAGE_EXTENSIONS.includes(ext));
    if (t.endsWith("/*")) return type.startsWith(t.slice(0, -1));
    if (t === "image/jpg") return type === "image/jpeg" || type === "image/jpg";
    return type === t;
  });
}

/** A short phrase for "what is accepted here", for messages: ".pdf" -> "PDF", "image/*" -> "image". */
export function describeAccept(accept: string | undefined): string {
  if (!accept) return "supported";
  const names = new Set<string>();
  for (const raw of accept.split(",")) {
    const t = raw.trim().toLowerCase();
    if (!t || t === "*") continue;
    if (t === "image/*") names.add("image");
    else if (t.startsWith(".")) names.add(t);
    else if (t === "application/pdf") names.add(".pdf");
    else if (t === "image/png" || t === "image/jpeg" || t === "image/jpg") names.add("image");
    else names.add(t);
  }
  const list = Array.from(names);
  if (list.length === 0) return "supported";
  if (list.length <= 6) return list.length > 1 ? `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}` : list[0];
  return "supported";
}

/** Splits dropped files into the ones this zone takes and the ones it turns away. */
export function partitionByAccept<T extends { name: string; type: string }>(files: T[], accept: string | undefined): { ok: T[]; rejected: T[] } {
  const ok: T[] = [];
  const rejected: T[] = [];
  for (const f of files) (matchesAccept(f, accept) ? ok : rejected).push(f);
  return { ok, rejected };
}
